import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeColors {
  // Background colors
  background: string;
  surface: string;
  surfaceElevated: string;
  
  // Text colors
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  
  // Border colors
  border: string;
  borderLight: string;
  borderHover: string;
  
  // Interactive colors
  buttonBackground: string;
  buttonHover: string;
  buttonText: string;
  
  // Send button
  sendButton: string;
  sendButtonHover: string;
  
  // Chat specific
  userMessageBg: string;
  assistantMessageBg: string;
  
  // Header/Navigation
  headerBg: string;
  headerText: string;
  
  // Sidebar
  sidebarBg: string;
  sidebarText: string;
  sidebarHover: string;
  
  // Status colors
  primary: string;
  success: string;
  successBg: string;
  errorHover: string;
}

const lightTheme: ThemeColors = {
  background: '#f9fafc',
  surface: '#ffffff',
  surfaceElevated: '#ffffff',
  
  textPrimary: '#2d3748',
  textSecondary: '#4a5568',
  textMuted: '#718096',
  
  border: '#e4e7eb',
  borderLight: 'rgba(228, 231, 235, 0.6)',
  borderHover: '#cbd5e0',
  
  buttonBackground: '#f9fafc',
  buttonHover: '#f1f5f9',
  buttonText: '#4a5568',
  
  sendButton: '#1f2937',
  sendButtonHover: '#374151',
  
  userMessageBg: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
  assistantMessageBg: '#ffffff',
  
  headerBg: '#ffffff',
  headerText: '#2d3748',
  
  sidebarBg: '#ffffff',
  sidebarText: '#4a5568',
  sidebarHover: '#f7fafc',
  
  primary: '#0078d4',
  success: '#16a34a',
  successBg: '#dcfce7',
  errorHover: '#ef4444'
};

const darkTheme: ThemeColors = {
  background: '#0f172a',
  surface: '#1e293b',
  surfaceElevated: '#334155',
  
  textPrimary: '#f1f5f9',
  textSecondary: '#cbd5e1',
  textMuted: '#94a3b8',
  
  border: '#334155',
  borderLight: 'rgba(51, 65, 85, 0.6)',
  borderHover: '#475569',
  
  buttonBackground: '#334155',
  buttonHover: '#475569',
  buttonText: '#cbd5e1',
  
  sendButton: '#3b82f6',
  sendButtonHover: '#2563eb',
  
  userMessageBg: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
  assistantMessageBg: '#1e293b',
  
  headerBg: '#1e293b',
  headerText: '#f1f5f9',
  
  sidebarBg: '#1e293b',
  sidebarText: '#cbd5e1',
  sidebarHover: '#334155',
  
  primary: '#3b82f6',
  success: '#22c55e',
  successBg: '#064e3b',
  errorHover: '#f87171'
};

interface ThemeContextType {
  theme: ThemeMode;
  colors: ThemeColors;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as ThemeMode) || 'light';
  });

  const colors = theme === 'light' ? lightTheme : darkTheme;

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);
  };

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
  };

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Layout effect so the variables exist before the first paint; otherwise themed CSS flashes in.
  useLayoutEffect(() => {
    // Lets plain CSS (hover states, animations) use the theme without JS handlers.
    Object.entries(colors).forEach(([name, value]) => {
      document.documentElement.style.setProperty(`--cv-${name}`, value);
    });
  }, [colors]);

  const value = {
    theme,
    colors,
    toggleTheme,
    setTheme
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};